import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError } from 'axios';
import React, { useContext, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { Color } from '@/constants/color';
import AppContext from '@/contexts/appContext';
import useCreateInsuranceVerificationFromExternal from '@/hooks/useCreateInsuranceVerificationFromExternal';
import useInsurancePriorityTypes from '@/hooks/useInsurancePriorityTypes';
import useUpdateInsurancePolicy from '@/hooks/useUpdateInsurancePolicy/useUpdateInsurancePolicy';
import PersonContext from '@/pages/Person/PersonContext';
import Button from '@/ui/components/Button';
import DatePicker from '@/ui/components/Form/Datepicker';
import InputField from '@/ui/components/Form/InputField';
import Label from '@/ui/components/Form/Label';
import RadioGroup from '@/ui/components/Form/RadioGroup';
import RichTextEditor from '@/ui/components/Form/RichTextEditor';
import { OptionType } from '@/ui/components/Form/Select/types';
import { currencyDollarsToCentsFormatter, parseStringToDollars } from '@/utils/currencies';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';

import { InsuranceVOBFormValues, insuranceVobSchema } from '../vobSchema';

import { Props } from './types';

const SaveVOBForm = ({
  externalPolicyVerificationId,
  lastVOBNotes,
  dismissPolicyVerification,
  planEndDate,
  insurancePriorityType,
  insurancePolicyId
}: Props) => {
  const { control, handleSubmit } = useForm({
    values: {
      vob_notes: lastVOBNotes ?? '',
      plan_end_date: planEndDate,
      priority_type: insurancePriorityType,
      estimated_monthly_cost: null,
      estimated_initial_month_cost: null
    },
    resolver: yupResolver(insuranceVobSchema),
    mode: 'onChange'
  });

  const { updatePerson } = useContext(PersonContext);
  const { addMessage } = useContext(AppContext);

  const { mutateAsync: createInsuranceVerification, isPending: isCreatingInsuranceVerification } =
    useCreateInsuranceVerificationFromExternal();

  const { mutateAsync: updateInsurancePolicy, isPending: isUpdatingInsurancePolicy } =
    useUpdateInsurancePolicy();

  const { data: priorityTypes } = useInsurancePriorityTypes();

  const [priorityTypesOptions, setPriorityTypesOptions] = useState<OptionType<string>[] | null>();

  useEffect(() => {
    if (priorityTypes?.data) {
      const formattedOptions = priorityTypes.data.map((priorityType) => ({
        label: priorityType,
        value: priorityType
      }));
      setPriorityTypesOptions(formattedOptions);
    }
  }, [priorityTypes]);

  const updatePolicy = async (values: InsuranceVOBFormValues) => {
    const hasPlanEndDateChanged = planEndDate !== values.plan_end_date;
    const hasPriorityTypeChanged = insurancePriorityType !== values.priority_type;
    if (hasPlanEndDateChanged || hasPriorityTypeChanged) {
      await updateInsurancePolicy({
        insurancePolicyId,
        values: { plan_end_date: values.plan_end_date, priority_type: values.priority_type }
      });
    }
  };

  const saveVob = async (values: InsuranceVOBFormValues) => {
    try {
      await updatePolicy(values);
      await createInsuranceVerification({
        data: {
          external_policy_verification_id: externalPolicyVerificationId,
          mutable_fields: {
            vob_notes: values.vob_notes || undefined,
            estimated_monthly_cost_in_cents: currencyDollarsToCentsFormatter(
              values.estimated_monthly_cost
            ),
            estimated_initial_month_cost_in_cents: currencyDollarsToCentsFormatter(
              values.estimated_initial_month_cost
            )
          }
        }
      });
      // Update person to update insurance_policies and last VOB
      updatePerson();
      dismissPolicyVerification();
    } catch (error) {
      addMessage(mapAxiosErrorMessages(error as AxiosError));
    }
  };

  return (
    <form className="flex flex-col mt-4 gap-2" onSubmit={handleSubmit(saveVob)}>
      <Controller
        name="plan_end_date"
        control={control}
        render={({ field: { onChange, value, name } }) => {
          return (
            <DatePicker
              isClearable
              label="Plan end date"
              name={name}
              onChange={onChange}
              value={value ?? ''}
            />
          );
        }}
      />
      {priorityTypesOptions && (
        <Controller
          name="priority_type"
          control={control}
          render={({ field: { onChange, value, name } }) => {
            return (
              <RadioGroup
                id={name}
                label="Order"
                options={priorityTypesOptions}
                selectedValue={value ?? ''}
                onChange={onChange}
              />
            );
          }}
        />
      )}
      <Label htmlFor="vob_notes" text="VOB Notes">
        <Controller
          name="vob_notes"
          control={control}
          render={({ field: { onChange, value } }) => {
            return <RichTextEditor onChange={onChange} value={value} />;
          }}
        />
      </Label>
      <Controller
        name="estimated_initial_month_cost"
        control={control}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <>
            <InputField
              label="Estimated Initial Month Cost"
              type="number"
              inputMode="decimal"
              typeHelper="dollars"
              requiredField={true}
              id="estimated_initial_month_cost"
              placeholder="0.00"
              step={0.01}
              min={0}
              onChange={(e) => {
                const inputVal = e.target.value.trim();
                onChange(inputVal === '' ? null : parseStringToDollars(inputVal));
              }}
              value={value === null ? '' : String(value)}
            />
            {error && <span className="text-red-500 text-sm">{error.message}</span>}
          </>
        )}
      />
      <Controller
        name="estimated_monthly_cost"
        control={control}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <>
            <InputField
              label="Estimated Monthly Cost"
              type="number"
              inputMode="decimal"
              typeHelper="dollars"
              requiredField={true}
              id="estimated_monthly_cost"
              placeholder="0.00"
              step={0.01}
              min={0}
              onChange={(e) => {
                const inputVal = e.target.value.trim();
                onChange(inputVal === '' ? null : parseStringToDollars(inputVal));
              }}
              value={value === null ? '' : String(value)}
            />
            {error && <span className="text-red-500 text-sm">{error.message}</span>}
          </>
        )}
      />
      <Button
        ariaLabel="Save VOB"
        type="submit"
        color={Color.WHITE}
        size="sm"
        className="flex-1"
        loading={isCreatingInsuranceVerification || isUpdatingInsurancePolicy}
        loadingWithSpinner
        disabled={isCreatingInsuranceVerification || isUpdatingInsurancePolicy}>
        Save VOB
      </Button>
    </form>
  );
};
export default React.memo(SaveVOBForm);
